import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import {
  BarChart as BarIcon,
  Calendar,
  ChevronDown,
  Clock,
  Download,
  Filter,
  PieChart as PieIcon,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { adminService } from '@/services';
import type {
  DeductionAnalysis,
  LateComersReport,
  MonthlySummary,
  PayrollDeductions,
} from '@/types';
import { DEPARTMENTS, formatEmployeeId, formatMinutes, formatNaira } from '@/utils';
import { cn } from '@/utils/cn';

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export function Reports() {
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState('deductions');

  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [deductionAnalysis, setDeductionAnalysis] = useState<DeductionAnalysis | null>(null);
  const [payrollData, setPayrollData] = useState<PayrollDeductions | null>(null);
  const [lateComers, setLateComers] = useState<LateComersReport | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const year = selectedMonth.year();
        const month = selectedMonth.month() + 1;

        const [summary, deductions, payroll, late] = await Promise.all([
          adminService.getMonthlySummary(year, month),
          adminService.getDeductionAnalysis(year, month),
          adminService.getPayrollDeductions(year, month, selectedDepartment),
          adminService.getLateComers(year, month, 3),
        ]);

        setMonthlySummary(summary);
        setDeductionAnalysis(deductions);
        setPayrollData(payroll);
        setLateComers(late);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, selectedDepartment]);

  const deductionTypeData = deductionAnalysis
    ? [
        {
          name: 'Late Arrival',
          value: deductionAnalysis.byType.late_arrival || 0,
        },
        { name: 'Extra Late', value: deductionAnalysis.byType.extra_late || 0 },
        {
          name: 'Missed Clock-Out',
          value: deductionAnalysis.byType.missed_clock_out || 0,
        },
      ].filter((d) => d.value > 0)
    : [];

  const departmentData = deductionAnalysis
    ? Object.entries(deductionAnalysis.byDepartment).map(([dept, amount]) => ({
        department: dept,
        amount,
      }))
    : [];

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    const lineHeight = 7;
    const sectionSpacing = 10;

    const checkNewPage = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
      }
    };

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Neolab Medical Laboratory', pageWidth / 2, yPos, { align: 'center' });
    yPos += lineHeight * 1.5;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text('Analytics & Reports', pageWidth / 2, yPos, { align: 'center' });
    yPos += lineHeight;

    doc.setFontSize(10);
    doc.text(`Report Period: ${selectedMonth.format('MMMM YYYY')}`, pageWidth / 2, yPos, {
      align: 'center',
    });
    yPos += lineHeight;
    if (selectedDepartment) {
      doc.text(`Department: ${selectedDepartment}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += lineHeight;
    }
    doc.text(`Generated: ${dayjs().format('MMMM DD, YYYY [at] HH:mm')}`, pageWidth / 2, yPos, {
      align: 'center',
    });
    yPos += sectionSpacing * 2;

    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary Statistics', margin, yPos);
    yPos += lineHeight * 1.5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const summaryStats = [
      {
        label: 'Staff Active',
        value: `${monthlySummary?.overview?.staffWithAttendance || 0} / ${monthlySummary?.overview?.totalStaff || 0}`,
      },
      {
        label: 'Total Attendance Logs',
        value: String(monthlySummary?.overview?.totalAttendanceRecords || 0),
      },
      {
        label: 'Punctuality Rate',
        value: `${(monthlySummary?.punctuality?.punctualityRate || 0).toFixed(1)}%`,
      },
      {
        label: 'Total Deductions',
        value: formatNaira(monthlySummary?.deductions?.total || 0),
      },
    ];

    summaryStats.forEach((stat) => {
      checkNewPage(lineHeight * 2);
      doc.setFont('helvetica', 'bold');
      doc.text(`${stat.label}:`, margin + 5, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(stat.value, pageWidth - margin - 5, yPos, { align: 'right' });
      yPos += lineHeight * 1.2;
    });
    yPos += sectionSpacing;

    if (deductionAnalysis) {
      checkNewPage(60);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Deduction Analysis', margin, yPos);
      yPos += lineHeight * 1.5;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('By Type:', margin + 5, yPos);
      yPos += lineHeight;

      doc.setFont('helvetica', 'normal');
      deductionTypeData.forEach((item) => {
        checkNewPage(lineHeight * 2);
        doc.text(`  • ${item.name}:`, margin + 10, yPos);
        doc.text(formatNaira(item.value), pageWidth - margin - 5, yPos, { align: 'right' });
        yPos += lineHeight * 1.2;
      });
      yPos += lineHeight;

      if (departmentData.length > 0) {
        checkNewPage(30);
        doc.setFont('helvetica', 'bold');
        doc.text('By Department:', margin + 5, yPos);
        yPos += lineHeight;

        doc.setFont('helvetica', 'normal');
        departmentData.forEach((item) => {
          checkNewPage(lineHeight * 2);
          doc.text(`  • ${item.department}:`, margin + 10, yPos);
          doc.text(formatNaira(item.amount), pageWidth - margin - 5, yPos, { align: 'right' });
          yPos += lineHeight * 1.2;
        });
      }
      yPos += sectionSpacing;

      if (deductionAnalysis.topDeductors && deductionAnalysis.topDeductors.length > 0) {
        checkNewPage(40);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Top Deductors', margin, yPos);
        yPos += lineHeight * 1.5;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Employee Name', margin + 5, yPos);
        doc.text('Total Deductions', pageWidth - margin - 5, yPos, { align: 'right' });
        yPos += lineHeight;

        doc.setFont('helvetica', 'normal');
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
        yPos += lineHeight * 0.5;

        deductionAnalysis.topDeductors.slice(0, 10).forEach((item) => {
          checkNewPage(lineHeight * 2);
          doc.text(item.name || 'N/A', margin + 5, yPos);
          doc.text(formatNaira(item.total || 0), pageWidth - margin - 5, yPos, { align: 'right' });
          yPos += lineHeight * 1.2;
        });
        yPos += sectionSpacing;
      }
    }

    if (lateComers?.lateComers && lateComers.lateComers.length > 0) {
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Staff with 3+ Late Arrivals', margin, yPos);
      yPos += lineHeight * 1.5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const colX = [margin + 5, margin + 70, margin + 125, margin + 160, pageWidth - margin - 5];
      doc.text('Employee', colX[0], yPos);
      doc.text('Dept', colX[1], yPos);
      doc.text('Count', colX[2], yPos);
      doc.text('Late Time', colX[3], yPos);
      doc.text('Deductions', colX[4], yPos, { align: 'right' });
      yPos += lineHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
      yPos += lineHeight * 0.5;

      doc.setFont('helvetica', 'normal');
      lateComers.lateComers.slice(0, 15).forEach((record) => {
        checkNewPage(lineHeight * 2);
        const name = record.user?.name || 'N/A';
        const dept = record.user?.department || 'N/A';
        const count = String(record.count || 0);
        const lateTime = formatMinutes(record.totalMinutes || 0);
        const deductions = formatNaira(record.totalDeductions || 0);

        doc.text(name.length > 20 ? `${name.substring(0, 20)}...` : name, colX[0], yPos);
        doc.text(dept.length > 10 ? `${dept.substring(0, 10)}...` : dept, colX[1], yPos);
        doc.text(count, colX[2], yPos);
        doc.text(lateTime, colX[3], yPos);
        doc.text(deductions, colX[4], yPos, { align: 'right' });
        yPos += lineHeight * 1.2;
      });
      yPos += sectionSpacing;
    }

    if (payrollData) {
      checkNewPage(60);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Payroll Deductions Summary', margin, yPos);
      yPos += lineHeight * 1.5;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Staff Evaluated: ${payrollData.summary?.totalStaff || 0}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.text(
        `Grand Total Deductions: ${formatNaira(payrollData.summary?.grandTotalDeductions || 0)}`,
        margin + 5,
        yPos,
      );
      yPos += lineHeight * 1.5;

      if (payrollData.staff && payrollData.staff.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const payrollColX = [
          margin + 5,
          margin + 70,
          margin + 125,
          margin + 160,
          pageWidth - margin - 5,
        ];
        doc.text('Employee', payrollColX[0], yPos);
        doc.text('Dept', payrollColX[1], yPos);
        doc.text('Days', payrollColX[2], yPos);
        doc.text('Late Days', payrollColX[3], yPos);
        doc.text('Deductions', payrollColX[4], yPos, { align: 'right' });
        yPos += lineHeight;

        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
        yPos += lineHeight * 0.5;

        doc.setFont('helvetica', 'normal');
        payrollData.staff.slice(0, 20).forEach((item) => {
          checkNewPage(lineHeight * 2);
          const name = item.name || 'N/A';
          const dept = item.department || 'N/A';
          const days = String(item.totalDays || 0);
          const lateDays = String(item.lateDays || 0);
          const deductions = formatNaira(item.netDeductions || 0);

          doc.text(name.length > 20 ? `${name.substring(0, 20)}...` : name, payrollColX[0], yPos);
          doc.text(dept.length > 10 ? `${dept.substring(0, 10)}...` : dept, payrollColX[1], yPos);
          doc.text(days, payrollColX[2], yPos);
          doc.text(lateDays, payrollColX[3], yPos);
          doc.text(deductions, payrollColX[4], yPos, { align: 'right' });
          yPos += lineHeight * 1.2;
        });
      }
    }

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    const dateStr = dayjs().format('YYYY-MM-DD');
    const filename = `neolab-reports-${dateStr}.pdf`;

    doc.save(filename);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
        <p className="text-slate-500 font-medium animate-pulse">Generating analytical reports...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Analytics & Reports
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Deep dive into workforce performance and financial metrics.
          </p>
        </motion.div>

        <button
          type="button"
          onClick={exportToPDF}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-neolab-green text-white font-black text-xs shadow-lg shadow-green-500/20 hover:scale-[1.02] active:scale-95 transition-all"
        >
          <Download className="w-4 h-4" />
          Export PDF Report
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm flex flex-col md:flex-row gap-4"
      >
        <div className="flex-1 flex gap-4">
          <div className="relative flex-1 md:flex-none">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="month"
              value={selectedMonth.format('YYYY-MM')}
              onChange={(e) => setSelectedMonth(dayjs(e.target.value))}
              className="w-full pl-10 pr-4 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold cursor-pointer"
            />
          </div>

          <div className="relative flex-1 md:flex-none">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value || undefined)}
              className="w-full pl-10 pr-10 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold appearance-none cursor-pointer min-w-[200px]"
            >
              <option value="">All Departments</option>
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Staff Active',
            value: monthlySummary?.overview?.staffWithAttendance || 0,
            total: monthlySummary?.overview?.totalStaff || 0,
            icon: Users,
            color: 'text-blue-500',
            bg: 'bg-blue-500/10',
          },
          {
            label: 'Total Logs',
            value: monthlySummary?.overview?.totalAttendanceRecords || 0,
            icon: Clock,
            color: 'text-indigo-500',
            bg: 'bg-indigo-500/10',
          },
          {
            label: 'Punctuality',
            value: `${(monthlySummary?.punctuality?.punctualityRate || 0).toFixed(1)}%`,
            icon: TrendingUp,
            color: 'text-emerald-500',
            bg: 'bg-emerald-500/10',
          },
          {
            label: 'Total Deductions',
            value: formatNaira(monthlySummary?.deductions?.total || 0),
            icon: Wallet,
            color: 'text-rose-500',
            bg: 'bg-rose-500/10',
          },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-4 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={cn('p-2 rounded-xl', stat.bg)}>
                <stat.icon className={cn('w-4 h-4', stat.color)} />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                {stat.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {stat.value}
              </p>
              {stat.total && (
                <span className="text-xs font-bold text-slate-400">/ {stat.total}</span>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="space-y-6">
        <div className="flex gap-2 p-1.5 rounded-[1.5rem] bg-neolab-green/5 dark:bg-white/5 w-fit">
          {[
            { id: 'deductions', label: 'Deduction Analysis', icon: PieIcon },
            { id: 'latecomers', label: 'Late Comers', icon: Clock },
            { id: 'payroll', label: 'Payroll Data', icon: Wallet },
          ].map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-black transition-all',
                activeTab === tab.id
                  ? 'bg-white dark:bg-neolab-forest text-neolab-green shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'deductions' && (
            <motion.div
              key="deductions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-4"
            >
              <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm">
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-4 tracking-tight flex items-center gap-2">
                  <PieIcon className="w-4 h-4 text-neolab-green" />
                  By Deduction Type
                </h3>
                <div className="h-[300px] w-full">
                  {deductionTypeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deductionTypeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                        >
                          {deductionTypeData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            borderRadius: '1rem',
                            border: 'none',
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          }}
                          formatter={(value) => formatNaira(Number(value))}
                        />
                        <Legend verticalAlign="bottom" iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400 font-bold">
                      No data available
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm">
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-4 tracking-tight flex items-center gap-2">
                  <BarIcon className="w-4 h-4 text-blue-500" />
                  By Department
                </h3>
                <div className="h-[300px] w-full">
                  {departmentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={departmentData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="department"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fontWeight: 700 }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fontWeight: 700 }}
                          tickFormatter={(value) => `₦${value.toLocaleString()}`}
                        />
                        <Tooltip
                          cursor={{ fill: '#f8fafc' }}
                          contentStyle={{
                            borderRadius: '1rem',
                            border: 'none',
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          }}
                          formatter={(value) => formatNaira(Number(value))}
                        />
                        <Bar dataKey="amount" fill="#22c55e" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400 font-bold">
                      No data available
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden">
                <div className="px-4 py-4 border-b border-neolab-green/5 dark:border-white/5">
                  <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                    Top Deductors
                  </h3>
                </div>
                <div className="p-2">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-xs font-black text-slate-400 uppercase tracking-widest">
                        <th className="px-4 py-3">Employee Name</th>
                        <th className="px-4 py-3 text-right">Total Deductions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neolab-green/5 dark:divide-white/5">
                      {(deductionAnalysis?.topDeductors || []).map((item, i) => (
                        <tr
                          key={i}
                          className="hover:bg-neolab-green/5 dark:hover:bg-white/5 transition-colors"
                        >
                          <td className="px-4 py-3 text-xs font-bold text-slate-700 dark:text-slate-200">
                            {item.name}
                          </td>
                          <td className="px-4 py-3 text-right font-black text-sm text-rose-500">
                            {formatNaira(item.total || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'latecomers' && (
            <motion.div
              key="latecomers"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-4 border-b border-neolab-green/5 dark:border-white/5">
                <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                  Staff with 3+ Late Arrivals
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-neolab-green/[0.02] dark:bg-white/5 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Department</th>
                      <th className="px-4 py-3 text-center">Late Count</th>
                      <th className="px-4 py-3 text-center">Total Late Time</th>
                      <th className="px-4 py-3 text-right">Total Deductions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neolab-green/5 dark:divide-white/5">
                    {(lateComers?.lateComers || []).map((record, i) => (
                      <tr
                        key={i}
                        className="hover:bg-neolab-green/5 dark:hover:bg-white/5 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-black text-slate-900 dark:text-white">
                            {record.user.name}
                          </p>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                            ID: {formatEmployeeId(record.user.employeeId)}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-bold text-slate-600 dark:text-slate-400 bg-neolab-green/5 dark:bg-white/5 px-2 py-1 rounded-lg">
                            {record.user.department}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-black text-amber-500 bg-amber-500/10 px-2 py-1 rounded-lg">
                            {record.count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-xs text-slate-600 dark:text-slate-400">
                          {formatMinutes(record.totalMinutes)}
                        </td>
                        <td className="px-4 py-3 text-right font-black text-sm text-rose-500">
                          {formatNaira(record.totalDeductions)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'payroll' && (
            <motion.div
              key="payroll"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-neolab-forest text-white shadow-xl">
                  <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-1">
                    Total Staff Evaluated
                  </p>
                  <p className="text-2xl font-black">{payrollData?.summary?.totalStaff || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-rose-500 text-white shadow-xl">
                  <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-1">
                    Grand Total Deductions
                  </p>
                  <p className="text-2xl font-black">
                    {formatNaira(payrollData?.summary?.grandTotalDeductions || 0)}
                  </p>
                </div>
              </div>

              <div className="rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-neolab-green/[0.02] dark:bg-white/5 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                        <th className="px-4 py-3">Employee</th>
                        <th className="px-4 py-3">Department</th>
                        <th className="px-4 py-3 text-center">Days Present</th>
                        <th className="px-4 py-3 text-center">Late Days</th>
                        <th className="px-4 py-3 text-right">Net Deductions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {(payrollData?.staff || []).map((item, i) => (
                        <tr
                          key={i}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-black text-slate-900 dark:text-white">
                              {item.name}
                            </p>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                              ID: {formatEmployeeId(item.employeeId)}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-xs font-bold text-slate-500">
                            {item.department}
                          </td>
                          <td className="px-4 py-3 text-center font-black text-xs text-slate-900 dark:text-white">
                            {item.totalDays}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={cn(
                                'text-xs font-black px-2 py-1 rounded-lg',
                                item.lateDays > 0
                                  ? 'text-rose-500 bg-rose-500/10'
                                  : 'text-emerald-500 bg-emerald-500/10',
                              )}
                            >
                              {item.lateDays}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-black text-sm text-rose-500">
                            {formatNaira(item.netDeductions)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-neolab-green/[0.02] dark:bg-white/5">
                        <td
                          colSpan={4}
                          className="px-4 py-3 text-xs font-black text-slate-900 dark:text-white uppercase tracking-widest"
                        >
                          Grand Total
                        </td>
                        <td className="px-4 py-3 text-right font-black text-xl text-rose-500">
                          {formatNaira(payrollData?.summary?.grandTotalDeductions || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default Reports;
